/**
 * Oktagon customizer (THEME.md §7.13; product page spec C1–C8; T-15; T-166, D-101).
 *
 * <okt-product-customizer> (blocks/okt-product-customizer.liquid), the name field:
 * - Keeps the field's validity in step with the customizer entry's rules (setCustomValidity), so
 *   Horizon's add to cart (form.checkValidity) and the browser (its bubble) stop on them. On an add
 *   attempt the field gets an inline error (role="alert") and aria-invalid, and takes the focus.
 * - Horizon's sticky add-to-cart bar clicks the main button from out of view and then plays its
 *   "added" animation, so its click is checked first and stopped while the name is invalid.
 * - Letter case "all_caps" (the default) turns the value itself into capitals, so the order shows
 *   what the preview shows. Curly apostrophes from phone keyboards become straight ones.
 * - Puts an <okt-text-preview> on every gallery photo that is the product's preview photo, found
 *   by media id, else by file name: the grid, the phone carousel and the zoom dialog. Horizon
 *   replaces the gallery after a variant change (shopify:product:select); the previews come back.
 * - Shows the name while it's typed (T-166, D-101; spec "Oktagon Customizer"), on print views: the preview
 *   photo zoomed to the print area (the entry's box with room around it), never smaller than the whole photo.
 *   - From 990 px, the spotlight (CU1, CU4–CU6): while the field has focus, the stage covers the media column,
 *     its view pinned under the header, growing from the photo's place in the grid in 250 ms (a fade under
 *     reduced motion). Blur, Esc or "All photos" put the gallery back; clicks on the stage keep the focus.
 *   - Below 990 px, the block's Mobile layout. Preview panel (C; CU7, CU8): the view above the field; a tap
 *     opens Horizon's zoom view on the photo. While the field has focus (a tap, Tab, VoiceOver, an add with an
 *     invalid name), the view and the field become the editing area (T-168, D-103): pinned over the page and its
 *     header to what the browser reports as visible (visualViewport), the whole photo above the field, which sits
 *     just above the keyboard; the page behind is held still, never scrolled, and is where it was afterwards.
 *     Bottom sheet (B; CU3): the field moves into Horizon's dialog, opened by the "Add your name" button; Confirm
 *     checks the rules and closes, closing otherwise (the X, Esc, a tap outside) puts the name back as it was. The
 *     sheet stays above the on-screen keyboard (visualViewport) and holds the page still, and an add with an
 *     invalid name opens it with the error.
 * - With ?okt-vv=1 in the address, a small readout shows what the browser reports (innerHeight, visualViewport,
 *   scrollY, the editing area's or the sheet's box, the field's, the last events), for screenshots on a real phone.
 *
 * <okt-text-preview>, the name on the photo (a port of the live jersey-text-overlay, without
 * built-in numbers): fitted into the entry's box (% of the photo) up to the largest font size
 * (% of the photo width), turned by the entry's direction, in the theme's accent font. It follows
 * the image's object-fit and object-position, so it lines up on contain photos (desktop grid,
 * zoom) and cover photos (the phone carousel with the "hint" peek). Hidden from screen readers.
 */

import { isPointWithinElement, lockScroll, unlockScroll } from '@theme/utilities';

/** Letters (Latin, Latin-1 and Latin Extended-A: Czech, Slovak, German…), digits, space, . - ' & */
const ALLOWED_CHARACTERS = {
  letters_digits: /^[0-9A-Za-zÀ-ÖØ-öø-ſẞ .'&-]*$/,
};

/** Degrees, clockwise, for each direction of the entry */
const ROTATION = {
  left_to_right: 0,
  top_to_bottom: 90,
  right_to_left: 180,
  bottom_to_top: -90,
};

const SVG_NS = 'http://www.w3.org/2000/svg';

/** The spotlight's width; below it, the block's Mobile layout */
const WIDE = '(min-width: 990px)';

/** Horizon's dialog events (assets/dialog.js) */
const DIALOG_OPEN = 'dialog:open';
const DIALOG_CLOSE = 'dialog:close';

/** What's visible is read again this long (ms) after the editing area or the sheet opens: iOS Safari reports the
 * keyboard late while its bottom bar is expanded (WebKit 265578) */
const VIEWPORT_CHECKS = [0, 50, 150, 300, 600];

/** An on-screen keyboard covers at least this much of the window */
const KEYBOARD_MIN = 120;

/** A finger that moves further than this between touchstart and touchend drags, it doesn't tap */
const TAP_SLOP = 10;

/**
 * The header offset in px (THEME.md §7.1; D-106, T-171): where the pinned header's bottom edge goes, which the stage
 * view is pinned at (registered as a length in okt-tokens). Without registered properties, the header's measured
 * bottom edge.
 * @returns {number}
 */
function headerOffset() {
  const offset = parseFloat(getComputedStyle(document.body).getPropertyValue('--okt-header-offset'));
  if (Number.isFinite(offset)) return offset;
  return Math.max(0, document.querySelector('#header-component')?.getBoundingClientRect().bottom ?? 0);
}

/** @typedef {{ top: number, height: number, inner: number }} VisibleArea */

/**
 * What the browser reports as visible: visualViewport's offsetTop and height, and the window's innerHeight. `apply` gets
 * them on every visualViewport resize and scroll, window resize and orientationchange, and 0, 50, 150, 300 and 600 ms
 * after start(); unchanged values are skipped. It feeds CSS variables only, which never scroll the page, so following
 * Safari's scroll events can't start a loop with its own scrolling, as the page scroll did (T-166).
 */
class ViewportSync {
  /** @type {(area: VisibleArea) => void} */
  #apply;

  #last = '';

  /** @type {number[]} */
  #timers = [];

  #running = false;

  /** @param {(area: VisibleArea) => void} apply */
  constructor(apply) {
    this.#apply = apply;
  }

  start() {
    if (!this.#running) {
      this.#running = true;
      window.visualViewport?.addEventListener('resize', this.measure);
      window.visualViewport?.addEventListener('scroll', this.measure);
      window.addEventListener('resize', this.measure);
      window.addEventListener('orientationchange', this.measure);
    }
    this.measure();
    for (const timer of this.#timers) clearTimeout(timer);
    this.#timers = VIEWPORT_CHECKS.map((delay) => window.setTimeout(this.measure, delay));
  }

  stop() {
    if (!this.#running) return;
    this.#running = false;
    this.#last = '';
    for (const timer of this.#timers) clearTimeout(timer);
    this.#timers = [];
    window.visualViewport?.removeEventListener('resize', this.measure);
    window.visualViewport?.removeEventListener('scroll', this.measure);
    window.removeEventListener('resize', this.measure);
    window.removeEventListener('orientationchange', this.measure);
  }

  measure = () => {
    if (!this.#running) return;
    const viewport = window.visualViewport;
    const area = {
      top: Math.round(viewport ? viewport.offsetTop : 0),
      height: Math.floor(viewport ? viewport.height : window.innerHeight),
      inner: window.innerHeight,
    };
    const key = `${area.top} ${area.height} ${area.inner}`;
    if (key === this.#last) return;
    this.#last = key;
    this.#apply(area);
  };
}

/** The sheet or editing area holding the page @type {Set<HTMLElement>} */
const holders = new Set();

/** The box the current touch may scroll (the sheet on a short screen) @type {Element | null} */
let touchScroller = null;

/** @param {TouchEvent} event */
function onHeldTouchStart(event) {
  touchScroller = null;
  const touch = event.touches.length === 1 ? event.touches[0] : null;
  let node = event.target instanceof Element ? event.target : null;
  const holder = node && [...holders].find((element) => element.contains(node));
  // A touch on the sheet's backdrop targets the dialog: it scrolls nothing
  if (!touch || !holder || (node === holder && !isPointWithinElement(touch.clientX, touch.clientY, holder))) return;

  for (; node && holder.contains(node); node = node.parentElement) {
    const { overflowY } = getComputedStyle(node);
    if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight) {
      touchScroller = node;
      return;
    }
  }
}

/** @param {TouchEvent} event */
function onHeldTouchMove(event) {
  if (!event.cancelable || event.touches.length > 1 || touchScroller) return;
  // A drag on a selection moves its handles
  const target = event.target;
  if (target instanceof HTMLInputElement && target === document.activeElement && target.selectionStart !== target.selectionEnd) {
    return;
  }
  event.preventDefault();
}

/**
 * Holds the page still under the sheet or the editing area (D-103), with Horizon's lock (the root's overflow: hidden)
 * kept by their own code. That lock alone doesn't do it on iOS Safari: a drag still scrolls the page while Safari's
 * bars are collapsed, by design (WebKit 153852 comment 34, 240859), and pans it while the keyboard is up. So, as
 * react-aria's usePreventScroll does on iOS, a drag (touchmove) is stopped unless it starts in a box of the holder that
 * scrolls and overflows (the sheet on a short screen, which overscroll-behavior: contain keeps to itself; it doesn't
 * when nothing overflows, WebKit 243452). Two fingers (zoom) and a drag on a selection pass. Nothing is moved or
 * restyled, so nothing jumps on open or close.
 * @param {HTMLElement} holder
 */
function holdPage(holder) {
  if (!holders.size) {
    document.addEventListener('touchstart', onHeldTouchStart, { capture: true, passive: true });
    document.addEventListener('touchmove', onHeldTouchMove, { capture: true, passive: false });
  }
  holders.add(holder);
}

/** @param {HTMLElement} holder */
function releasePage(holder) {
  if (!holders.delete(holder) || holders.size) return;
  touchScroller = null;
  document.removeEventListener('touchstart', onHeldTouchStart, { capture: true });
  document.removeEventListener('touchmove', onHeldTouchMove, { capture: true });
}

/**
 * Puts an element in the top layer (a manual popover), above the header, Horizon's sticky bar and anything its section
 * stacks, without moving it in the DOM (the field keeps its focus and its form). Its own CSS keeps it displayed.
 * @param {HTMLElement} element
 */
function raise(element) {
  if (typeof element.showPopover !== 'function') return;
  try {
    if (element.matches(':popover-open')) element.hidePopover();
    element.setAttribute('popover', 'manual');
    element.showPopover();
  } catch {
    element.removeAttribute('popover');
  }
}

/** @param {HTMLElement} element */
function lower(element) {
  if (!element.hasAttribute('popover')) return;
  try {
    if (element.matches(':popover-open')) element.hidePopover();
  } catch {
    // Already hidden
  }
  element.removeAttribute('popover');
}

/**
 * ?okt-vv=1 (D-103): a small box in the top end corner of what's visible, with what the browser reports, for a
 * screenshot on a real phone: innerHeight; visualViewport's height, offsetTop and pageTop; scrollY; the editing area's or
 * the sheet's top and height; the field's top and bottom (all in the page's window, as getBoundingClientRect gives
 * them); and the last six events, in ms since the last focus or opening. Never shown without the flag.
 */
class ViewportReadout {
  /** @type {HTMLElement} */
  #box;

  /** @type {string[]} */
  #events = [];

  #since = performance.now();

  constructor() {
    const box = document.createElement('div');
    box.setAttribute('aria-hidden', 'true');
    box.style.cssText = [
      'position: fixed',
      'inset: calc(var(--okt-readout-top, 0px) + max(4px, env(safe-area-inset-top))) 4px auto auto',
      'z-index: 2147483647',
      'display: block',
      'box-sizing: border-box',
      'width: auto',
      'height: auto',
      'max-width: calc(100vw - 8px)',
      'margin: 0',
      'padding: 4px 6px',
      'overflow: hidden',
      'border: 0',
      'border-radius: 4px',
      'background: rgb(0 0 0 / 0.75)',
      'color: #fff',
      'font: 10px/1.3 ui-monospace, SFMono-Regular, Menlo, monospace',
      'white-space: pre',
      'pointer-events: none',
    ].join(';');
    this.#box = box;
    document.body.append(box);
    this.raise();

    const viewport = window.visualViewport;
    viewport?.addEventListener('resize', () => this.#event('vv.resize'));
    viewport?.addEventListener('scroll', () => this.#event('vv.scroll'));
    window.addEventListener('resize', () => this.#event('resize'));
    window.addEventListener('scroll', () => this.#event('scroll'), { passive: true });
    window.addEventListener('orientationchange', () => this.#event('orientation'));
    window.setInterval(() => this.render(), 250);
    this.render();
  }

  /** Back on top of a sheet or an editing area opened after it */
  raise() {
    raise(this.#box);
  }

  /**
   * @param {string} text
   * @param {boolean} [restart] - The times count from here
   */
  note(text, restart = false) {
    if (restart) this.#since = performance.now();
    this.#push(text, false);
  }

  /** @param {string} type */
  #event(type) {
    const viewport = window.visualViewport;
    const value =
      type.startsWith('vv') && viewport
        ? `${Math.round(viewport.height)}@${Math.round(viewport.offsetTop)}`
        : type === 'scroll'
          ? String(Math.round(window.scrollY))
          : String(window.innerHeight);
    this.#push(`${type} ${value}`, true);
  }

  /**
   * @param {string} text
   * @param {boolean} merge - A run of the same event keeps one line, with its last value
   */
  #push(text, merge) {
    const line = `+${Math.round(performance.now() - this.#since)} ${text}`;
    const last = this.#events[this.#events.length - 1];
    if (merge && last && last.split(' ')[1] === text.split(' ')[0]) this.#events.pop();
    this.#events.push(line);
    if (this.#events.length > 6) this.#events.shift();
    this.render();
  }

  render() {
    const round = Math.round;
    const viewport = window.visualViewport;
    const area = document.querySelector('.okt-product-customizer__editor--open');
    const sheet = document.querySelector('dialog.okt-product-customizer__dialog[open]');
    const field = document.querySelector('okt-product-customizer input[name="properties[Text]"]')?.getBoundingClientRect();
    const box = area ?? sheet;
    const rect = box?.getBoundingClientRect();
    this.#box.textContent = [
      `inner ${window.innerWidth}×${window.innerHeight}`,
      viewport
        ? `vv ${round(viewport.height)} top ${round(viewport.offsetTop)} page ${round(viewport.pageTop)}` +
          (Math.abs(viewport.scale - 1) > 0.01 ? ` ×${viewport.scale.toFixed(2)}` : '')
        : 'vv none',
      `scrollY ${round(window.scrollY)}`,
      rect ? `${area ? 'area' : 'sheet'} ${round(rect.top)} h ${round(rect.height)}` : 'area/sheet closed',
      field ? `field ${round(field.top)}–${round(field.bottom)}` : 'field none',
      ...this.#events,
    ].join('\n');
    this.#box.style.setProperty('--okt-readout-top', `${round(viewport?.offsetTop ?? 0)}px`);
  }
}

const readout = new URLSearchParams(window.location.search).has('okt-vv') ? new ViewportReadout() : null;

/**
 * @typedef {object} PreviewConfig
 * @property {string} mediaId
 * @property {string} file
 * @property {number} width - The preview photo's width in pixels
 * @property {number} height - The preview photo's height in pixels
 * @property {number[]} box - Left, top, width and height of the text box, in % of the photo
 * @property {number | null} fontMax - The largest font size, in % of the photo width
 * @property {string} direction
 * @property {string} color
 */

/**
 * @typedef {{ left: number, top: number, width: number, height: number }} Rect
 */

/** @param {string} url */
function fileName(url) {
  return (url || '').split('?')[0].split('/').pop() || '';
}

/**
 * Offset of a replaced element's content box inside its box, from a computed object-position.
 * @param {string} value - e.g. "50% 50%", "0px 100%", "center top"
 * @param {number} freeX - Box width minus drawn width (negative when the image overflows)
 * @param {number} freeY
 * @returns {[number, number]}
 */
function objectPositionOffset(value, freeX, freeY) {
  const keywords = { left: 0, top: 0, center: 0.5, right: 1, bottom: 1 };
  const parts = (value || '50% 50%').trim().split(/\s+/);
  const axis = (/** @type {string | undefined} */ token, /** @type {number} */ free) => {
    if (!token) return free / 2;
    if (token in keywords) return free * keywords[/** @type {keyof typeof keywords} */ (token)];
    if (token.endsWith('%')) return (free * parseFloat(token)) / 100;
    if (token.endsWith('px')) return parseFloat(token);
    return free / 2;
  };
  return [axis(parts[0], freeX), axis(parts[1] ?? parts[0], freeY)];
}

/**
 * The part of an image's box that its photo fills, from its object-fit and object-position.
 * @param {number} boxWidth
 * @param {number} boxHeight
 * @param {CSSStyleDeclaration} style - The image's computed style
 * @param {PreviewConfig} config
 * @returns {Rect}
 */
function fittedRect(boxWidth, boxHeight, style, config) {
  const fit = style.objectFit;
  let width = boxWidth;
  let height = boxHeight;

  if (fit !== 'fill') {
    const contain = Math.min(boxWidth / config.width, boxHeight / config.height);
    let scale = contain;
    if (fit === 'cover') scale = Math.max(boxWidth / config.width, boxHeight / config.height);
    if (fit === 'none') scale = 1;
    if (fit === 'scale-down') scale = Math.min(1, contain);
    width = config.width * scale;
    height = config.height * scale;
  }

  const [left, top] = objectPositionOffset(style.objectPosition, boxWidth - width, boxHeight - height);
  return { left, top, width, height };
}

/**
 * Where the photo sits in a print view of width × height: zoomed to the entry's box with `margin` (a share of the
 * box on each side) around it and centred on the box, never smaller than the whole photo, and covering the view
 * where it can. An infinite margin gives the whole photo, centred (the editing area).
 * @param {PreviewConfig} config
 * @param {number} width
 * @param {number} height
 * @param {number} margin
 * @returns {Rect}
 */
function printArea(config, width, height, margin) {
  const { width: photoWidth, height: photoHeight } = config;
  if (!Number.isFinite(margin)) {
    const whole = Math.min(width / photoWidth, height / photoHeight);
    return {
      left: (width - photoWidth * whole) / 2,
      top: (height - photoHeight * whole) / 2,
      width: photoWidth * whole,
      height: photoHeight * whole,
    };
  }
  const [x, y, boxWidth, boxHeight] = config.box.map((value, index) => (value / 100) * (index % 2 ? photoHeight : photoWidth));

  let cropWidth = boxWidth * (1 + 2 * margin);
  let cropHeight = boxHeight * (1 + 2 * margin);
  if (cropWidth / cropHeight < width / height) {
    cropWidth = (cropHeight * width) / height;
  } else {
    cropHeight = (cropWidth * height) / width;
  }

  const whole = Math.min(width / photoWidth, height / photoHeight);
  const scale = Math.max(whole, Math.min(width / cropWidth, height / cropHeight));
  const drawnWidth = photoWidth * scale;
  const drawnHeight = photoHeight * scale;
  const left = width / 2 - (x + boxWidth / 2) * scale;
  const top = height / 2 - (y + boxHeight / 2) * scale;

  return {
    left: drawnWidth >= width ? Math.min(0, Math.max(width - drawnWidth, left)) : (width - drawnWidth) / 2,
    top: drawnHeight >= height ? Math.min(0, Math.max(height - drawnHeight, top)) : (height - drawnHeight) / 2,
    width: drawnWidth,
    height: drawnHeight,
  };
}

/** @param {string} name @param {string} fallback */
function cssValue(name, fallback) {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback;
}

/** A CSS time ('250ms', '0.25s') in milliseconds, else the fallback @param {string} value @param {number} fallback */
function milliseconds(value, fallback) {
  const number = parseFloat(value);
  if (Number.isNaN(number)) return fallback;
  return value.endsWith('ms') ? number : number * 1000;
}

class OktTextPreview extends HTMLElement {
  /** @type {HTMLCanvasElement | undefined} */
  static #canvas;

  /** Fonts being loaded, and fonts whose load has settled (loaded or failed) @type {Map<string, Promise<void>>} */
  static #fonts = new Map();

  /** @type {Set<string>} */
  static #settled = new Set();

  #waiting = false;

  /** @type {SVGSVGElement | undefined} */
  #svg;

  /** @type {SVGTextElement | undefined} */
  #text;

  /** @type {HTMLImageElement | null} */
  #image = null;

  /** @type {ResizeObserver | undefined} */
  #observer;

  /** @type {PreviewConfig | null} */
  #config = null;

  #value = '';

  connectedCallback() {
    this.setAttribute('aria-hidden', 'true');

    if (!this.#svg) {
      this.#svg = /** @type {SVGSVGElement} */ (document.createElementNS(SVG_NS, 'svg'));
      this.#svg.setAttribute('preserveAspectRatio', 'none');
      this.#svg.setAttribute('focusable', 'false');
      this.#text = /** @type {SVGTextElement} */ (document.createElementNS(SVG_NS, 'text'));
      this.#svg.append(this.#text);
      this.append(this.#svg);
    }

    this.#image = this.parentElement?.querySelector(':scope > img') ?? null;
    if (this.#image) {
      // Also redraws: a preview first drawn while hidden (zoom dialog, phone carousel) is
      // measured again once it shows
      this.#observer = new ResizeObserver(() => {
        this.#place();
        this.#draw();
      });
      this.#observer.observe(this.#image);
    }

    this.#place();
    this.#draw();
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
  }

  /** @param {PreviewConfig} config */
  configure(config) {
    if (this.#config === config) return;
    this.#config = config;
    this.#place();
    this.#draw();
  }

  get value() {
    return this.#value;
  }

  set value(text) {
    if (text === this.#value) return;
    this.#value = text;
    this.#draw();
  }

  /** Lays the overlay over the image's box, and the drawing over the part the photo fills. */
  #place() {
    const image = this.#image;
    const config = this.#config;
    if (!image || !config || !this.#svg) return;

    const boxWidth = image.offsetWidth;
    const boxHeight = image.offsetHeight;
    if (!boxWidth || !boxHeight) return;

    this.style.left = `${image.offsetLeft}px`;
    this.style.top = `${image.offsetTop}px`;
    this.style.width = `${boxWidth}px`;
    this.style.height = `${boxHeight}px`;

    const drawn = fittedRect(boxWidth, boxHeight, getComputedStyle(image), config);
    const svgStyle = this.#svg.style;
    svgStyle.left = `${drawn.left}px`;
    svgStyle.top = `${drawn.top}px`;
    svgStyle.width = `${drawn.width}px`;
    svgStyle.height = `${drawn.height}px`;
  }

  #draw() {
    const config = this.#config;
    const text = this.#text;
    if (!config || !text || !this.#svg) return;

    const { width, height } = config;
    this.#svg.setAttribute('viewBox', `0 0 ${width} ${height}`);
    this.style.setProperty('--okt-text-preview-fill', config.color);

    const [left, top, boxWidth, boxHeight] = [
      (config.box[0] / 100) * width,
      (config.box[1] / 100) * height,
      (config.box[2] / 100) * width,
      (config.box[3] / 100) * height,
    ];
    const centerX = left + boxWidth / 2;
    const centerY = top + boxHeight / 2;
    const angle = ROTATION[/** @type {keyof typeof ROTATION} */ (config.direction)] ?? 0;
    const across = Math.abs(angle) === 90;

    text.setAttribute('x', String(centerX));
    text.setAttribute('y', String(centerY));
    if (angle) {
      text.setAttribute('transform', `rotate(${angle} ${centerX} ${centerY})`);
    } else {
      text.removeAttribute('transform');
    }

    const value = this.#value;
    if (text.textContent !== value) text.textContent = value;
    if (!value.trim()) return;

    const fontMax = config.fontMax ? (config.fontMax / 100) * width : Infinity;
    const along = across ? boxHeight : boxWidth;
    const thickness = across ? boxWidth : boxHeight;
    const size = this.#fit(value, along, thickness, fontMax);
    text.setAttribute('font-size', String(Math.round(size * 100) / 100));

    // The canvas measure is close; the drawn text's own box (when it's rendered) settles it
    const drawn = text.getBBox();
    if (drawn.width > 0 && drawn.height > 0) {
      const shrink = Math.min(1, along / drawn.width, thickness / drawn.height);
      if (shrink < 1) text.setAttribute('font-size', String(Math.floor(size * shrink * 100) / 100));
    }
  }

  /**
   * The largest font size, in photo pixels, at which the text fits the box.
   * @param {string} value
   * @param {number} length - The box side the text runs along
   * @param {number} thickness - The other side
   * @param {number} fontMax
   */
  #fit(value, length, thickness, fontMax) {
    const text = /** @type {SVGTextElement} */ (this.#text);
    const style = getComputedStyle(text);
    const font = `${style.fontStyle} ${style.fontWeight} 100px ${style.fontFamily}`;

    // Until the accent font has loaded, the size is measured on the fallback; redraw once it's in
    if (!OktTextPreview.#settled.has(font) && !this.#waiting && !document.fonts.check(font, value)) {
      if (!OktTextPreview.#fonts.has(font)) {
        const loading = document.fonts
          .load(font, value)
          .catch(() => {})
          .then(() => {
            OktTextPreview.#settled.add(font);
          });
        OktTextPreview.#fonts.set(font, loading);
      }
      this.#waiting = true;
      OktTextPreview.#fonts.get(font)?.then(() => {
        this.#waiting = false;
        this.#draw();
      });
    }

    const canvas = (OktTextPreview.#canvas ??= document.createElement('canvas'));
    const context = canvas.getContext('2d');
    if (!context) return Math.min(fontMax, thickness / 1.2);

    context.font = font;
    const metrics = context.measureText(value);
    const ink = (metrics.actualBoundingBoxLeft || 0) + (metrics.actualBoundingBoxRight || 0);
    const perPixelLength = Math.max(metrics.width, ink) / 100;
    const perPixelThickness = ((metrics.fontBoundingBoxAscent || 0) + (metrics.fontBoundingBoxDescent || 0)) / 100 || 1.2;

    return Math.max(0, Math.min(fontMax, length / perPixelLength, thickness / perPixelThickness));
  }
}

if (!customElements.get('okt-text-preview')) {
  customElements.define('okt-text-preview', OktTextPreview);
}

class OktProductCustomizer extends HTMLElement {
  /** @type {AbortController | undefined} */
  #controller;

  /** @type {MutationObserver | undefined} */
  #observer;

  /** Lays out the print views when their size changes @type {ResizeObserver | undefined} */
  #views;

  #frame = 0;

  /** @type {HTMLInputElement | null} */
  #input = null;

  /** @type {HTMLElement | null} */
  #error = null;

  /** @type {PreviewConfig | null} */
  #preview = null;

  #showing = false;

  #wide = window.matchMedia(WIDE);

  // Spotlight (from 990 px)

  /** @type {HTMLElement | null} */
  #stage = null;

  /** @type {HTMLElement | null} */
  #stageView = null;

  /** @type {HTMLElement | null} */
  #stageHost = null;

  #spotlight = false;

  /** @type {Animation[]} */
  #stageAnimations = [];

  // Bottom sheet (below 990 px)

  /** Horizon's dialog-component @type {(HTMLElement & { showDialog?: () => void, closeDialog?: () => void }) | null} */
  #sheet = null;

  /** @type {HTMLDialogElement | null} */
  #dialog = null;

  /** @type {HTMLButtonElement | null} */
  #opener = null;

  /** @type {HTMLElement | null} */
  #openerValue = null;

  /** @type {HTMLElement | null} */
  #openerError = null;

  #saved = '';

  #confirmed = false;

  #opening = false;

  #reportOnOpen = false;

  /** Keeps the sheet above the keyboard */
  #sheetSync = new ViewportSync((area) => this.#placeSheet(area));

  /** A touch that started outside the open sheet @type {{ x: number, y: number } | null} */
  #outsideTouch = null;

  // Preview panel and its editing area (below 990 px)

  /** @type {HTMLElement | null} */
  #panel = null;

  /** The panel and the field: the editing area while the field has focus @type {HTMLElement | null} */
  #editor = null;

  #editing = false;

  /** The page's scroll when the editing area opened @type {{ x: number, y: number }} */
  #pageScroll = { x: 0, y: 0 };

  /** Keeps the editing area on what's visible */
  #editorSync = new ViewportSync((area) => this.#placeEditor(area));

  connectedCallback() {
    this.#input = this.querySelector('.okt-product-customizer__input');
    this.#error = this.querySelector('.okt-product-customizer__field .okt-product-customizer__error');
    if (!this.#input) return;

    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const input = this.#input;

    input.addEventListener('input', this.#onInput, { signal });
    input.addEventListener('compositionend', this.#onInput, { signal });
    input.addEventListener('change', this.#onChange, { signal });
    input.addEventListener('invalid', this.#onInvalid, { signal });
    input.addEventListener('focus', this.#onFocus, { signal });
    window.addEventListener('click', this.#onAddClick, { capture: true, signal });

    this.#preview = this.#readPreview();
    if (this.#preview) {
      this.#scope.addEventListener('shopify:product:select', this.#onProductSelect, { signal });
      this.#observer = new MutationObserver((records) => {
        const outside = records.some(
          (record) => !(record.target instanceof Element && record.target.closest('okt-text-preview'))
        );
        if (outside) this.#schedule();
      });
      this.#observer.observe(this.#scope, { childList: true, subtree: true });
    }

    // Quick add never shows the block (customizable products open their page), but just in case: no views there
    if (!this.closest('dialog, .quick-add-modal__content')) this.#setUpViews(signal);

    this.#normalize();
    this.#validate();
    this.#updateCount();
    this.#updateOpener();
    this.#attach();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#observer?.disconnect();
    this.#views?.disconnect();
    this.#stopSheet();
    this.#closeEditor();
    cancelAnimationFrame(this.#frame);
    for (const animation of this.#stageAnimations) animation.cancel();
    this.#spotlight = false;
    this.#stage?.remove();
  }

  /** The product's section (or the quick-add dialog): where its gallery and sticky bar live. */
  get #scope() {
    return /** @type {HTMLElement} */ (this.closest('.shopify-section, dialog') ?? document.body);
  }

  /** @returns {PreviewConfig | null} */
  #readPreview() {
    const data = this.dataset;
    const box = (data.previewBox || '').split(',').map(Number);
    const width = Number(data.previewWidth);
    const height = Number(data.previewHeight);
    if (!data.previewMediaId || box.length !== 4 || box.some((n) => !Number.isFinite(n))) return null;
    if (!(width > 0 && height > 0)) return null;

    return {
      mediaId: data.previewMediaId,
      file: data.previewFile || '',
      width,
      height,
      box,
      fontMax: Number(data.previewFontMax) || null,
      direction: data.previewDirection || 'left_to_right',
      color: data.previewColor || '',
    };
  }

  get #capitals() {
    return this.dataset.letterCase !== 'as_typed';
  }

  /** @param {AbortSignal} signal */
  #setUpViews(signal) {
    const input = /** @type {HTMLInputElement} */ (this.#input);

    this.#views = new ResizeObserver((entries) => {
      for (const entry of entries) this.#layoutView(/** @type {HTMLElement} */ (entry.target));
    });
    for (const view of this.querySelectorAll('.okt-product-customizer__view')) this.#views.observe(view);

    input.addEventListener('blur', this.#onBlur, { signal });
    input.addEventListener('keydown', this.#onKeyDown, { signal });
    input.addEventListener('click', this.#onFieldClick, { signal });
    this.#wide.addEventListener('change', this.#onWideChange, { signal });
    window.addEventListener('resize', this.#onResize, { signal });
    this.#panel = this.querySelector('.okt-product-customizer__panel');
    this.#editor = this.#panel ? this.querySelector('.okt-product-customizer__editor') : null;
    if (this.#panel && this.#editor) {
      this.#panel.addEventListener('click', this.#onPanelClick, { signal });
      // While editing, a press anywhere in the area but the field keeps the focus in the field
      this.#editor.addEventListener('pointerdown', this.#onEditorPress, { signal });
      this.#editor.addEventListener('mousedown', this.#onEditorPress, { signal });
      this.querySelector('.okt-product-customizer__done')?.addEventListener('click', this.#endEditing, { signal });
    }

    const sheet = this.querySelector('dialog-component.okt-product-customizer__sheet');
    const dialog = sheet?.querySelector('dialog');
    if (sheet instanceof HTMLElement && dialog instanceof HTMLDialogElement) {
      this.#sheet = sheet;
      this.#dialog = dialog;
      this.#opener = this.querySelector('.okt-product-customizer__opener');
      this.#openerValue = this.querySelector('.okt-product-customizer__opener-value');
      this.#openerError = this.querySelector('.okt-product-customizer__opener-group .okt-product-customizer__error');
      this.#opener?.addEventListener('click', () => this.#openSheet(), { signal });
      this.querySelector('.okt-product-customizer__confirm')?.addEventListener('click', this.#confirm, { signal });
      sheet.addEventListener(DIALOG_OPEN, this.#onSheetOpen, { signal });
      sheet.addEventListener(DIALOG_CLOSE, this.#onSheetClose, { signal });
      this.#placeField();
    }

    // The stage goes in now, hidden, so its photo loads with the page
    if (this.#wide.matches) this.#ensureStage();
  }

  /** @param {Event} [event] */
  #onInput = (event) => {
    if (event instanceof InputEvent && event.isComposing) return;
    this.#normalize();
    this.#validate();
    this.#updateCount();
    this.#updatePreviews();
    this.#updateOpener();
  };

  #onChange = () => {
    const input = /** @type {HTMLInputElement} */ (this.#input);
    const trimmed = input.value.trim();
    if (trimmed !== input.value) input.value = trimmed;
    this.#validate();
    this.#updateCount();
    this.#updatePreviews();
    this.#updateOpener();
  };

  #onInvalid = () => {
    const input = /** @type {HTMLInputElement} */ (this.#input);
    this.#show(input.validationMessage || this.#message());
    if (this.#sheetMode) {
      if (!this.#dialog?.open) {
        this.#openSheet({ report: true });
      } else if (document.activeElement !== input) {
        input.focus({ preventScroll: true });
      }
      return;
    }
    // Below 990 px the focus opens the editing area, which shows the field with its error without scrolling the page
    if (!this.#panelMode) this.#reveal();
    if (document.activeElement !== input) input.focus({ preventScroll: true });
  };

  /**
   * Brings the whole field (label to error) into view below the sticky header, so neither the
   * header nor the sticky add-to-cart bar hides it; the browser's own scroll only reaches the edge.
   */
  #reveal() {
    const rect = this.getBoundingClientRect();
    const header = Math.max(0, headerOffset());
    if (rect.top >= header && rect.bottom <= window.innerHeight) return;
    this.scrollIntoView({ block: 'center', behavior: 'instant' });
  }

  /**
   * The sticky bar adds through the main button, out of view: stop it while the name is invalid,
   * then show the field with its error and the browser's bubble. With the bottom sheet the field is in the
   * closed sheet, so the main button is stopped too and the sheet opens with the error.
   * @param {MouseEvent} event
   */
  #onAddClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const button = target.closest('button');
    const input = this.#input;
    if (!(button instanceof HTMLButtonElement) || button.disabled || !input || !input.form) return;

    if (button.matches('sticky-add-to-cart [ref="addToCartButton"]')) {
      const bar = /** @type {HTMLElement | null} */ (button.closest('sticky-add-to-cart'));
      if (bar?.dataset.productId !== this.dataset.productId) return;
    } else if (!(this.#sheetMode && button.type === 'submit' && button.form === input.form)) {
      return;
    }
    if (input.validity.valid) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    if (this.#sheetMode) {
      this.#show(input.validationMessage || this.#message());
      this.#openSheet({ report: true });
    } else {
      input.reportValidity();
    }
  };

  /**
   * From 990 px the spotlight opens; below it, with Preview panel, the editing area. On phones the carousel behind
   * moves to the preview photo, so the name shows there afterwards.
   */
  #onFocus = () => {
    readout?.note('focus', true);
    this.#openSpotlight();
    this.#openEditor();

    const slide = this.#targets()
      .map((media) => media.closest('slideshow-slide'))
      .find(Boolean);
    const slideshow = /** @type {(HTMLElement & {select?: Function, current?: number}) | null | undefined} */ (
      slide?.closest('slideshow-component')
    );
    if (!slide || !slideshow || !slideshow.checkVisibility?.() || typeof slideshow.select !== 'function') return;

    const index = Array.from(slideshow.querySelectorAll('slideshow-slide')).indexOf(slide);
    if (index >= 0 && slideshow.current !== index) slideshow.select(index, undefined, { animate: false });
  };

  #onBlur = () => {
    readout?.note('blur');
    this.#closeSpotlight();
    this.#closeEditor();
  };

  /** A click in the focused field brings back a spotlight or an editing area closed with Esc (or "All photos"). */
  #onFieldClick = () => {
    if (document.activeElement !== this.#input) return;
    this.#openSpotlight();
    this.#openEditor();
  };

  /** @param {KeyboardEvent} event */
  #onKeyDown = (event) => {
    if (event.isComposing) return;
    if (event.key === 'Escape' && this.#editing) {
      // As with the spotlight, Esc closes the area and leaves the focus in the field
      event.preventDefault();
      this.#closeEditor();
    } else if (event.key === 'Escape' && this.#spotlight) {
      event.preventDefault();
      this.#closeSpotlight();
    } else if (event.key === 'Enter' && this.#editing) {
      // The keyboard's Done key (enterkeyhint="done") ends editing; it doesn't add to the cart from there
      event.preventDefault();
      this.#endEditing();
    } else if (event.key === 'Enter' && this.#dialog?.open) {
      // In the sheet, Enter confirms the name instead of adding to the cart
      event.preventDefault();
      this.#confirm();
    }
  };

  #onWideChange = () => {
    if (this.#wide.matches) {
      this.#closeEditor();
      this.#ensureStage();
    } else {
      this.#closeSpotlight({ instant: true });
    }
    // An open sheet closes itself from 990 px (Horizon's dialog-active-max-width); the field moves after that
    if (!this.#dialog?.open) this.#placeField();
  };

  #onResize = () => {
    if (this.#stage?.isConnected) this.#sizeStage();
  };

  /** @param {Event & {promise?: Promise<unknown>}} event */
  #onProductSelect = (event) => {
    event.promise?.then(() => this.#schedule()).catch(() => {});
  };

  #schedule() {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#attach());
  }

  #normalize() {
    const input = /** @type {HTMLInputElement} */ (this.#input);
    let value = input.value.replace(/[‘’ʼ]/g, "'");
    if (this.#capitals) value = value.toUpperCase();
    if (input.maxLength > 0 && value.length > input.maxLength) value = value.slice(0, input.maxLength);
    if (value === input.value) return;

    const { selectionStart, selectionEnd } = input;
    input.value = value;
    if (selectionStart !== null && selectionEnd !== null && document.activeElement === input) {
      input.setSelectionRange(Math.min(selectionStart, value.length), Math.min(selectionEnd, value.length));
    }
  }

  /** The entry's rules, in order: required, allowed characters, not the placeholder. */
  #message() {
    const input = /** @type {HTMLInputElement} */ (this.#input);
    const value = input.value.trim();
    if (!value) return input.required ? this.dataset.errorRequired || '' : '';

    const pattern = ALLOWED_CHARACTERS[/** @type {keyof typeof ALLOWED_CHARACTERS} */ (this.dataset.allowedCharacters)];
    if (pattern && !pattern.test(value)) return this.dataset.errorCharacters || '';

    const placeholder = (this.dataset.placeholder || '').trim();
    if (this.dataset.rejectPlaceholder !== 'false' && placeholder && value.toUpperCase() === placeholder.toUpperCase()) {
      return this.dataset.errorPlaceholder || '';
    }

    return '';
  }

  #validate() {
    const message = this.#message();
    /** @type {HTMLInputElement} */ (this.#input).setCustomValidity(message);
    // Once shown, the error follows the typing; a character that isn't allowed shows at once
    if (this.#showing || (message && message === this.dataset.errorCharacters)) this.#show(message);
  }

  /** @param {string} message */
  #show(message) {
    const input = /** @type {HTMLInputElement} */ (this.#input);
    this.#showing = Boolean(message);
    for (const error of [this.#error, this.#openerError]) {
      if (error && error.textContent !== message) error.textContent = message;
    }
    this.#opener?.classList.toggle('okt-product-customizer__opener--invalid', Boolean(message));
    if (message) {
      input.setAttribute('aria-invalid', 'true');
    } else {
      input.removeAttribute('aria-invalid');
    }
  }

  #updateCount() {
    const input = /** @type {HTMLInputElement} */ (this.#input);
    const count = this.querySelector('.okt-product-customizer__count');
    if (!count) return;
    const used = String(input.value.length);
    const max = String(input.maxLength);
    const visible = count.querySelector('[aria-hidden="true"]');
    const spoken = count.querySelector('.visually-hidden');
    if (visible) visible.textContent = `${used}/${max}`;
    if (spoken && this.dataset.countTemplate) {
      spoken.textContent = this.dataset.countTemplate.replace('[current]', used).replace('[max]', max);
    }
  }

  /** The field's text, or the placeholder while it's empty, in the entry's letter case. */
  #previewText() {
    const input = /** @type {HTMLInputElement} */ (this.#input);
    const text = input.value.trim() ? input.value : this.dataset.placeholder || '';
    return this.#capitals ? text.toUpperCase() : text;
  }

  /** The gallery's photos that are the preview photo: by media id, else by file name. */
  #targets() {
    const preview = this.#preview;
    if (!preview) return [];
    const scope = this.#scope;
    /** @type {HTMLElement[]} */
    let media = Array.from(scope.querySelectorAll(`div.product-media[data-media-id="${CSS.escape(preview.mediaId)}"]`));

    if (!media.length && preview.file) {
      media = Array.from(scope.querySelectorAll('div.product-media')).filter((element) => {
        const image = element.querySelector(':scope > img');
        return image instanceof HTMLImageElement && fileName(image.getAttribute('src') || '') === preview.file;
      });
    }

    return media.filter((element) => element.querySelector(':scope > img'));
  }

  /** The print views' photos: the panel's or the sheet's, and the stage's. */
  #viewPhotos() {
    const views = Array.from(this.querySelectorAll('.okt-product-customizer__view'));
    if (this.#stageView) views.push(this.#stageView);
    return views
      .map((view) => view.querySelector(':scope > .okt-product-customizer__photo'))
      .filter((photo) => photo instanceof HTMLElement && photo.querySelector(':scope > img'));
  }

  #attach() {
    const preview = this.#preview;
    if (!preview) return;
    const text = this.#previewText();

    for (const media of [...this.#targets(), ...this.#viewPhotos()]) {
      let overlay = /** @type {OktTextPreview | null} */ (media.querySelector(':scope > okt-text-preview'));
      if (!overlay) {
        overlay = /** @type {OktTextPreview} */ (document.createElement('okt-text-preview'));
        overlay.configure(preview);
        overlay.value = text;
        media.append(overlay);
      } else {
        overlay.configure(preview);
        overlay.value = text;
      }
    }
  }

  #updatePreviews() {
    const text = this.#previewText();
    for (const overlay of this.#scope.querySelectorAll('okt-text-preview')) {
      /** @type {OktTextPreview} */ (overlay).value = text;
    }
  }

  /**
   * Zooms a print view's photo to the print area (the view's zoom margin: 1 on the stage, 0.25 in the panel and the
   * sheet; the whole photo in the editing area) and asks for a photo as wide as it's drawn.
   * @param {HTMLElement} view
   */
  #layoutView(view) {
    const preview = this.#preview;
    const photo = view.querySelector(':scope > .okt-product-customizer__photo');
    const width = view.clientWidth;
    const height = view.clientHeight;
    if (!preview || !(photo instanceof HTMLElement) || !width || !height) return;

    const margin = this.#editing && view === this.#panel ? Infinity : Number(view.dataset.zoomMargin) || 0;
    const area = printArea(preview, width, height, margin);
    photo.style.left = `${area.left}px`;
    photo.style.top = `${area.top}px`;
    photo.style.width = `${area.width}px`;
    photo.style.height = `${area.height}px`;
    // Shown from now on: CSS hides it until it's in place, so it never jumps there while the page loads (T-200)
    photo.toggleAttribute('data-placed', true);

    const image = photo.querySelector(':scope > img');
    if (image instanceof HTMLImageElement) image.sizes = `${Math.ceil(area.width)}px`;
  }

  // ───────────── Preview panel and its editing area ─────────────

  /** A tap on the panel opens Horizon's zoom view on the preview photo, as a tap on the photo does (CU8). */
  #onPanelClick = (/** @type {MouseEvent} */ event) => {
    if (this.#editing) return;
    const gallery = /** @type {(HTMLElement & { zoom?: Function }) | null} */ (this.#scope.querySelector('media-gallery'));
    const zoomed = this.#targets().find((media) => media.closest('zoom-dialog'));
    const item = zoomed?.closest('li');
    if (!gallery || !item || typeof gallery.zoom !== 'function') return;

    const index = Array.from(item.parentElement?.children ?? []).indexOf(item);
    if (index >= 0) gallery.zoom(index, event);
  };

  /** The preview panel is in use: the layout is Preview panel and the screen is below 990 px. */
  get #panelMode() {
    return Boolean(this.#panel) && !this.#wide.matches;
  }

  /**
   * The editing area (T-168, D-103): below 990 px with Preview panel, while the field has focus, however it got it (a
   * tap, Tab, VoiceOver, an add with an invalid name), the panel and the field cover what the browser reports as visible,
   * header included, in the top layer: the whole photo above, the label with Done, the field, the hint and the counter
   * below, just above the keyboard. The page is never scrolled: iOS Safari's own scroll and pan with the keyboard raced
   * every scroll of ours (T-166; WebKit 265578, 300523, 311821; Apple forums thread 800125). The area follows every
   * report of what's visible instead, through CSS variables only, and the page behind is held still. The block keeps its
   * height in the page, so nothing behind moves, and the page's scroll is put back if the browser moved it meanwhile.
   */
  #openEditor() {
    const editor = this.#editor;
    const input = this.#input;
    if (this.#editing || !editor || !input || !this.#panelMode || document.activeElement !== input) return;

    this.#editing = true;
    this.#pageScroll = { x: window.scrollX, y: window.scrollY };
    this.style.setProperty('--okt-editor-space', `${this.offsetHeight}px`);
    this.classList.add('okt-product-customizer--editing');
    editor.classList.add('okt-product-customizer__editor--open');
    this.#editorSync.start();
    raise(editor);
    lockScroll(editor);
    holdPage(editor);
    if (this.#panel) this.#layoutView(this.#panel);
    readout?.note('area open');
    readout?.raise();
  }

  /** @param {VisibleArea} area */
  #placeEditor(area) {
    const editor = this.#editor;
    if (!editor) return;
    editor.style.setProperty('--okt-vv-top', `${area.top}px`);
    editor.style.setProperty('--okt-vv-height', `${area.height}px`);
    editor.toggleAttribute('data-keyboard', area.inner - area.height >= KEYBOARD_MIN);
  }

  /**
   * The area lets go at once, with every value it took from visualViewport (iOS 26 leaves offsetTop and the height
   * wrong after the keyboard closes, Apple forums thread 800125); the field is back in the page, which is where it was.
   */
  #closeEditor() {
    const editor = this.#editor;
    if (!this.#editing || !editor) return;
    this.#editing = false;

    this.#editorSync.stop();
    releasePage(editor);
    unlockScroll(editor);
    lower(editor);
    editor.classList.remove('okt-product-customizer__editor--open');
    editor.removeAttribute('data-keyboard');
    editor.style.removeProperty('--okt-vv-top');
    editor.style.removeProperty('--okt-vv-height');
    this.classList.remove('okt-product-customizer--editing');
    this.style.removeProperty('--okt-editor-space');

    const { x, y } = this.#pageScroll;
    if (Math.abs(window.scrollX - x) >= 1 || Math.abs(window.scrollY - y) >= 1) {
      window.scrollTo({ left: x, top: y, behavior: 'instant' });
    }
    if (this.#panel) this.#layoutView(this.#panel);
    readout?.note('area close');
  }

  /** Done, or the keyboard's Done key: the field lets go of the focus, which closes the keyboard and the area. */
  #endEditing = () => {
    readout?.note('done');
    this.#input?.blur();
    this.#closeEditor();
  };

  /**
   * While editing, a press on the photo, the label, the hint or Done keeps the focus (and the keyboard) in the field.
   * @param {Event} event
   */
  #onEditorPress = (event) => {
    if (this.#editing && event.target !== this.#input) event.preventDefault();
  };

  // ───────────── Spotlight ─────────────

  /**
   * The stage: from the template into the product grid (its parent from then on), where CSS places it over the
   * media's column, the photos and the blocks below them ("Below the photos"); hidden until the field has focus.
   */
  #ensureStage() {
    if (!this.#preview) return null;
    if (this.#stage?.isConnected) return this.#stage;

    const gallery = this.#scope.querySelector('media-gallery');
    const host = gallery?.closest('.product-information__grid') ?? gallery?.parentElement;
    if (!(host instanceof HTMLElement)) return null;

    if (!this.#stage) {
      const template = this.querySelector(':scope > template.okt-product-customizer__stage-template');
      const stage = template instanceof HTMLTemplateElement ? template.content.firstElementChild?.cloneNode(true) : null;
      if (!(stage instanceof HTMLElement)) return null;

      this.#stage = stage;
      this.#stageView = stage.querySelector('.okt-product-customizer__stage-view');
      // A press on the stage keeps the focus in the field; "All photos" puts the gallery back
      stage.addEventListener('pointerdown', (event) => event.preventDefault());
      stage.querySelector('.okt-product-customizer__all')?.addEventListener('click', () => this.#closeSpotlight());
    }

    host.classList.add('okt-product-customizer__stage-host');
    host.append(this.#stage);
    this.#stageHost = host;
    this.#sizeStage();
    if (this.#stageView) this.#views?.observe(this.#stageView);
    this.#attach();
    return this.#stage;
  }

  /**
   * The view's height: from its top (pinned under the header once the page scrolls) to the screen's bottom edge, as
   * far as the gallery goes; a gallery shorter than it is wide gets a square view. At least 240 px. It's set with the
   * header offset the view is pinned at added (where the header is going, not where a slide has it now); CSS takes
   * the offset off again, so the view follows the header (D-106, T-171).
   */
  #sizeStage() {
    const stage = this.#stage;
    const view = this.#stageView;
    const host = this.#stageHost;
    if (!stage || !view || !host) return;

    // The media's column, which the stage covers (the host is the whole product grid)
    const rect = stage.getBoundingClientRect();
    if (!rect.width) return;
    const gallery = this.#scope.querySelector('media-gallery')?.getBoundingClientRect().height || 0;
    const pinned = headerOffset();
    const top = Math.max(rect.top, pinned);
    const height = Math.max(240, Math.min(window.innerHeight - top, Math.max(rect.width, gallery)));
    stage.style.setProperty('--okt-stage-height', `${Math.round(height + pinned)}px`);
  }

  #openSpotlight() {
    if (this.#spotlight || !this.#wide.matches || this.#dialog?.open) return;
    const stage = this.#ensureStage();
    const view = this.#stageView;
    if (!stage || !view) return;

    this.#spotlight = true;
    this.#sizeStage();
    this.#layoutView(view);
    stage.classList.add('okt-product-customizer__stage--open');
    this.#animateStage(true);
  }

  /** @param {{ instant?: boolean }} [options] */
  #closeSpotlight({ instant = false } = {}) {
    if (!this.#spotlight) return;
    this.#spotlight = false;

    if (instant) {
      for (const animation of this.#stageAnimations) animation.cancel();
      this.#stage?.classList.remove('okt-product-customizer__stage--open');
      return;
    }
    this.#animateStage(false);
  }

  /**
   * Grows the view from the photo's place in the grid, or shrinks it back (CU6): the view's clip goes from the
   * grid cell to the whole view while the photo moves from where the grid draws it to its zoomed place. Under
   * reduced motion, with Minimal drawers, or when the cell isn't behind the stage, the view fades.
   * @param {boolean} opening
   */
  #animateStage(opening) {
    const stage = this.#stage;
    const view = this.#stageView;
    const photo = view?.querySelector(':scope > .okt-product-customizer__photo');
    if (!stage || !view || !(photo instanceof HTMLElement)) return;

    for (const animation of this.#stageAnimations) animation.cancel();

    // The drawers' and sheets' motion tokens (THEME.md §8.2, §8.3 #12): the grow on their entry time and curve, the
    // shrink on their exit's. Under reduced motion and with Minimal drawers (no travel) the view only fades, on the
    // same times, which reduced motion keeps within 150 ms
    const still =
      window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
      parseFloat(cssValue('--okt-motion-drawers-travel', '1')) === 0;
    const start = still ? null : this.#cellStart(view, photo);
    const fadeIn = cssValue('--animation-timing-fade-in', 'cubic-bezier(0.4, 0, 0.2, 1)');
    const fadeOut = cssValue('--animation-timing-fade-out', 'cubic-bezier(0.4, 0, 0.2, 1)');
    const quad = cssValue('--ease-out-quad', 'cubic-bezier(0.32, 0.72, 0, 1)');
    const easing = start
      ? cssValue(opening ? '--okt-motion-drawers-ease' : '--okt-motion-drawers-ease-exit', quad)
      : opening
        ? fadeIn
        : fadeOut;
    const duration = milliseconds(
      cssValue(opening ? '--okt-motion-drawers' : '--okt-motion-drawers-exit', ''),
      opening ? 250 : 200
    );
    /** @type {KeyframeAnimationOptions} */
    const timing = { duration, easing, fill: opening ? 'none' : 'forwards' };

    if (start) {
      const clip = [start.clip, 'inset(0px)'];
      const transform = [start.transform, 'none'];
      if (!opening) {
        clip.reverse();
        transform.reverse();
      }
      this.#stageAnimations = [view.animate({ clipPath: clip }, timing), photo.animate({ transform }, timing)];
    } else {
      this.#stageAnimations = [view.animate({ opacity: opening ? [0, 1] : [1, 0] }, timing)];
    }

    if (opening) return;
    const animations = this.#stageAnimations;
    Promise.all(animations.map((animation) => animation.finished)).then(
      () => {
        if (this.#spotlight) return;
        stage.classList.remove('okt-product-customizer__stage--open');
        for (const animation of animations) animation.cancel();
      },
      () => {}
    );
  }

  /**
   * The start (or end) of the grow: the view clipped to the grid cell, and the photo's transform from its place in
   * the view to where the grid draws it. Null when the cell isn't behind the view.
   * @param {HTMLElement} view
   * @param {HTMLElement} photo
   */
  #cellStart(view, photo) {
    const preview = this.#preview;
    const media = this.#targets().find((element) => !element.closest('dialog') && element.checkVisibility?.());
    const image = media?.querySelector(':scope > img');
    if (!preview || !media || !(image instanceof HTMLImageElement)) return null;

    const cell = (media.closest('li, slideshow-slide') ?? media).getBoundingClientRect();
    const box = view.getBoundingClientRect();
    const top = Math.max(0, cell.top - box.top);
    const left = Math.max(0, cell.left - box.left);
    const right = Math.max(0, box.right - cell.right);
    const bottom = Math.max(0, box.bottom - cell.bottom);
    if (top + bottom >= box.height - 1 || left + right >= box.width - 1) return null;

    const imageBox = image.getBoundingClientRect();
    const drawn = fittedRect(imageBox.width, imageBox.height, getComputedStyle(image), preview);
    const place = photo.getBoundingClientRect();
    if (!drawn.width || !place.width) return null;

    const x = imageBox.left + drawn.left - place.left;
    const y = imageBox.top + drawn.top - place.top;
    return {
      clip: `inset(${top}px ${right}px ${bottom}px ${left}px)`,
      transform: `translate(${x}px, ${y}px) scale(${drawn.width / place.width})`,
    };
  }

  // ───────────── Bottom sheet ─────────────

  /** The sheet is in use: the layout is Bottom sheet and the screen is below 990 px. */
  get #sheetMode() {
    return Boolean(this.#dialog) && !this.#wide.matches;
  }

  /** Below 990 px the field lives in the sheet; from 990 px it's back in the page, before the button. */
  #placeField() {
    const field = this.querySelector('.okt-product-customizer__field');
    const slot = this.querySelector('.okt-product-customizer__sheet-field');
    const home = this.querySelector(':scope > .okt-product-customizer__editor');
    if (!field || !slot || !home) return;

    if (this.#sheetMode) {
      if (field.parentElement !== slot) slot.append(field);
    } else if (field.parentElement === slot) {
      home.append(field);
    }
  }

  /** @param {{ report?: boolean }} [options] - report: show the browser's bubble once the sheet is open */
  #openSheet({ report = false } = {}) {
    const sheet = this.#sheet;
    if (!sheet || !this.#dialog || this.#dialog.open || this.#opening || typeof sheet.showDialog !== 'function') return;

    this.#placeField();
    this.#opening = true;
    this.#reportOnOpen = report;
    this.#saved = /** @type {HTMLInputElement} */ (this.#input).value;
    this.#confirmed = false;
    this.#focusStandIn();
    sheet.showDialog();
  }

  /**
   * iOS shows the keyboard only for a focus made inside the tap, and Horizon opens the dialog a frame later, so a
   * stand-in field takes the focus until the dialog gives it to the name field; the keyboard stays up while the
   * focus moves from field to field. Touch screens only.
   */
  #focusStandIn() {
    if (!window.matchMedia('(pointer: coarse)').matches) return;
    const standIn = document.createElement('input');
    standIn.type = 'text';
    standIn.className = 'okt-product-customizer__stand-in';
    standIn.tabIndex = -1;
    standIn.setAttribute('aria-hidden', 'true');
    standIn.setAttribute('autocomplete', 'off');
    if (this.#capitals) standIn.setAttribute('autocapitalize', 'characters');
    standIn.addEventListener('blur', () => standIn.remove(), { once: true });
    this.append(standIn);
    standIn.focus({ preventScroll: true });
  }

  /**
   * The open sheet stays above the keyboard, holds the page still (Horizon's dialog locks the root, which iOS Safari
   * doesn't honour while the keyboard is up or its bars are collapsed) and closes on a tap outside.
   */
  #onSheetOpen = () => {
    this.#opening = false;
    this.querySelector(':scope > .okt-product-customizer__stand-in')?.remove();
    this.#sheetSync.start();
    if (this.#dialog) holdPage(this.#dialog);
    document.addEventListener('touchstart', this.#onSheetTouchStart, { capture: true, passive: true });
    document.addEventListener('touchend', this.#onSheetTouchEnd, { capture: true, passive: false });
    document.addEventListener('touchcancel', this.#onSheetTouchCancel, { capture: true, passive: true });
    readout?.note('sheet open', true);
    readout?.raise();

    const input = /** @type {HTMLInputElement} */ (this.#input);
    if (document.activeElement !== input) input.focus({ preventScroll: true });
    if (this.#reportOnOpen) {
      this.#reportOnOpen = false;
      input.reportValidity();
    }
  };

  #onSheetClose = () => {
    this.#opening = false;
    this.#stopSheet();
    readout?.note('sheet close');

    // Closed without Confirm (the X, Esc, a tap outside): the name as it was. From 990 px the typed name stays.
    const input = /** @type {HTMLInputElement} */ (this.#input);
    if (!this.#confirmed && !this.#wide.matches && input.value !== this.#saved) {
      input.value = this.#saved;
      this.#onInput();
    }
    this.#confirmed = false;
    this.#updateOpener();
    this.#placeField();
    if (!this.#wide.matches) this.#opener?.focus({ preventScroll: true });
  };

  /** Confirm: the entry's rules; an invalid name keeps the sheet open with the error and the bubble. */
  #confirm = () => {
    const input = /** @type {HTMLInputElement} */ (this.#input);
    const trimmed = input.value.trim();
    if (trimmed !== input.value) input.value = trimmed;
    this.#onInput();
    if (!input.reportValidity()) return;

    this.#confirmed = true;
    this.#sheet?.closeDialog?.();
  };

  /**
   * Keeps the sheet above the on-screen keyboard: iOS Safari and Chrome on Android shrink only the visual viewport,
   * so the sheet's bottom moves up by what the keyboard covers and its height stays within what's left.
   * @param {VisibleArea} area
   */
  #placeSheet(area) {
    const dialog = this.#dialog;
    if (!dialog) return;
    dialog.style.setProperty('--okt-sheet-bottom', `${Math.max(0, area.inner - area.top - area.height)}px`);
    dialog.style.setProperty('--okt-sheet-height', `${area.height}px`);
    dialog.toggleAttribute('data-keyboard', area.inner - area.height >= KEYBOARD_MIN);
  }

  /** Everything the open sheet set up goes at once, its visualViewport values too. */
  #stopSheet() {
    this.#sheetSync.stop();
    this.#outsideTouch = null;
    document.removeEventListener('touchstart', this.#onSheetTouchStart, { capture: true });
    document.removeEventListener('touchend', this.#onSheetTouchEnd, { capture: true });
    document.removeEventListener('touchcancel', this.#onSheetTouchCancel, { capture: true });
    const dialog = this.#dialog;
    if (!dialog) return;
    releasePage(dialog);
    dialog.removeAttribute('data-keyboard');
    dialog.style.removeProperty('--okt-sheet-bottom');
    dialog.style.removeProperty('--okt-sheet-height');
  }

  /**
   * A tap outside the sheet closes it as the X does, the name going back to what it was, as a tap beside the mobile
   * menu closes the menu. Horizon's dialog closes on the click that follows a tap, which iOS Safari doesn't reliably
   * deliver while the keyboard is up: the tap that dismissed the keyboard there left the sheet open (the user on an
   * iPhone, 13 Oct). So the touch itself decides, where it started: on the backdrop (the dialog, outside its box) or on
   * anything outside the dialog. Its click is stopped, so it can't land on the page once the sheet has gone.
   * @param {TouchEvent} event
   */
  #onSheetTouchStart = (event) => {
    const dialog = this.#dialog;
    const touch = event.touches.length === 1 ? event.touches[0] : null;
    const target = event.target;
    this.#outsideTouch = null;
    if (!dialog?.open || !touch || this.#opening || dialog.classList.contains('dialog-closing')) return;

    const inside =
      target instanceof Node &&
      dialog.contains(target) &&
      (target !== dialog || isPointWithinElement(touch.clientX, touch.clientY, dialog));
    if (!inside) this.#outsideTouch = { x: touch.clientX, y: touch.clientY };
  };

  /** @param {TouchEvent} event */
  #onSheetTouchEnd = (event) => {
    const start = this.#outsideTouch;
    const touch = event.changedTouches[0];
    this.#outsideTouch = null;
    if (!start || !touch || event.touches.length) return;
    if (Math.hypot(touch.clientX - start.x, touch.clientY - start.y) > TAP_SLOP) return;

    if (event.cancelable) event.preventDefault();
    readout?.note('tap outside');
    this.#input?.blur();
    this.#sheet?.closeDialog?.();
  };

  #onSheetTouchCancel = () => {
    this.#outsideTouch = null;
  };

  /** The button shows the confirmed name with Edit, or "Add your name". */
  #updateOpener() {
    const opener = this.#opener;
    const value = this.#openerValue;
    if (!opener || !value) return;

    const name = /** @type {HTMLInputElement} */ (this.#input).value.trim();
    const text = name || value.dataset.empty || '';
    opener.classList.toggle('okt-product-customizer__opener--filled', Boolean(name));
    if (value.textContent !== text) value.textContent = text;
  }
}

if (!customElements.get('okt-product-customizer')) {
  customElements.define('okt-product-customizer', OktProductCustomizer);
}
